import React, { useState } from 'react';
import { Col, Row, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import { RiWallet3Line } from 'react-icons/ri';
import { createRazorpayOrder, verifyRazorpay } from 'api/customer';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import * as _ from 'lodash';

const AddMoneyModal = ({ show, onHide, onSuccess }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > 500000) {
      toast.error('Amount exceeds maximum allowed payment limit of 5,00,000 INR');
      return;
    }

    setLoading(true);
    try {
      const res = await loadRazorpayScript();
      if (!res) {
        toast.error('Razorpay SDK failed to load. Are you online?');
        setLoading(false);
        return;
      }

      const orderRes = await createRazorpayOrder({ amount: parseFloat(amount) });
      const { razorpay_order_id, razorpay_key, invoice_id } = orderRes.data;

      const options = {
        key: razorpay_key,
        amount: parseFloat(amount) * 100,
        currency: 'INR',
        name: 'Charge Zone',
        description: 'Wallet Top-up',
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            const verifyRes = await verifyRazorpay(invoice_id, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.status === 200) {
              toast.success('Money added successfully');
              onSuccess();
              onHide();
            }
          } catch (error) {
            if (!_.get(error, 'data.message')) {
              toast.error('Payment verification failed');
            }
          }
        },
        theme: {
          color: '#3399cc',
        },
      };

      const paymentObject = new window.Razorpay(options);
      onHide();
      paymentObject.open();
    } catch (error) {
      if (!_.get(error, 'data.message')) {
        toast.error('Failed to initiate payment');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setAmount('');
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={onHide} onExited={resetState} centered className="add-money-modal">
      <div className="logout-modal-main_page">
        <div className="logout-modal--inner">
          <div className="logout-modal---block_title">
            <div className="inner--block_title">
              <div className="title">
                <RiWallet3Line size={30} color="#fff" />
              </div>
              <p className="sub-title">Add Money to Wallet</p>
            </div>
          </div>
          <div className="logout-modal-input-box__block p-4">
             <Row className="mb-4">
               <Col lg={12}>
                 <Input
                   label="Enter Amount (INR)"
                   placeholder="0.00"
                   type="number"
                   value={amount}
                   onChange={(e) => setAmount(e.target.value)}
                   required
                 />
               </Col>
             </Row>
            <Row>
              <Col lg={6}>
                <Button className="logout-model--btn cancel--btn w-100" onClick={onHide}>
                  {t('button.cancel')}
                </Button>
              </Col>
              <Col lg={6}>
                <Button 
                  className="logout-model--btn remove--btn w-100" 
                  onClick={handlePayment}
                  loading={loading}
                  disabled={loading || !amount}
                >
                  Pay Now
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    </Modal>
  );
};

AddMoneyModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddMoneyModal;
